import React, { useCallback, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface DemoInquiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  source?: string;
}

interface DemoInquiryForm {
  name: string;
  email: string;
  company: string;
  message: string;
}

const initialForm: DemoInquiryForm = {
  name: '',
  email: '',
  company: '',
  message: '',
};

export function DemoInquiryDialog({
  open,
  onOpenChange,
  title = 'Request a Demo',
  description = "Tell us about your firm and we'll schedule a personalized walkthrough.",
  source = 'marketing_inquiry',
}: DemoInquiryDialogProps) {
  const [form, setForm] = useState<DemoInquiryForm>(initialForm);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      setForm(initialForm);
      setStatus('idle');
    }
    onOpenChange(nextOpen);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const functions = getFunctions(app, 'us-central1');
      const submitInquiry = httpsCallable(functions, 'submitBriefingInquiry');
      await submitInquiry({ ...form, source });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [form, source]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-light leading-none text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="landing-body-md text-zinc-400">
            {description}
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="py-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-white font-semibold">Thank you.</p>
            <p className="text-zinc-400 text-sm">We'll be in touch shortly.</p>
            <Button
              variant="ghost"
              className="text-zinc-400 hover:text-white mt-2"
              onClick={() => handleOpenChange(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor={`${source}-name`} className="text-zinc-300">Name *</Label>
              <Input
                id={`${source}-name`}
                required
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${source}-email`} className="text-zinc-300">Email *</Label>
              <Input
                id={`${source}-email`}
                type="email"
                required
                placeholder="you@firm.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${source}-company`} className="text-zinc-300">Firm *</Label>
              <Input
                id={`${source}-company`}
                required
                placeholder="Firm name"
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${source}-message`} className="text-zinc-300">
                Message <span className="text-zinc-600">(optional)</span>
              </Label>
              <Textarea
                id={`${source}-message`}
                placeholder="What does your deal process look like today?"
                rows={3}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-zinc-700 resize-none"
              />
            </div>

            {status === 'error' && (
              <p className="text-sm text-red-400">
                Something went wrong. Please email us directly at{' '}
                <a href="mailto:adamrosemanlsa@gmail.com" className="underline">adamrosemanlsa@gmail.com</a>.
              </p>
            )}

            <Button
              type="submit"
              disabled={status === 'sending'}
              className="h-11 w-full rounded-[2px] border border-white/20 bg-white font-semibold text-[#09090B] hover:bg-[#A9C2B1]"
            >
              {status === 'sending' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
